import React, { useMemo } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { getTableActionsConfig } from '../../config/appConfig';
import { FileIcon } from '../ui/Icons';

// Simple Gantt-style timeline view
// Props:
// - columns (for first column title and field labels if needed)
// - data
// - startDateKey, endDateKey
// - dateScale: 'day' | 'week' | 'month'
// - onRowClick, actionsRenderer

const MS_IN_DAY = 24 * 60 * 60 * 1000;

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

const TimelineTable = ({
  columns = [],
  data = [],
  startDateKey,
  endDateKey,
  dateScale = 'week',
  onRowClick,
  actionsRenderer,
  className,
  onFileCountClick
}) => {
  const { theme } = useTheme();
  
  // Get table actions configuration
  const actionsVisibility = getTableActionsConfig('visibility');
  const showTooltips = getTableActionsConfig('showTooltips');
  const buttonSize = getTableActionsConfig('buttonSize');
  const spacing = getTableActionsConfig('spacing');

  const { days, start, end } = useMemo(() => {
    let minDate = null;
    let maxDate = null;
    for (const row of data) {
      const s = row[startDateKey] ? new Date(row[startDateKey]) : null;
      const e = row[endDateKey] ? new Date(row[endDateKey]) : null;
      if (s && !isNaN(s)) minDate = !minDate ? s : (s < minDate ? s : minDate);
      if (e && !isNaN(e)) maxDate = !maxDate ? e : (e > maxDate ? e : maxDate);
    }
    // fallback window
    const now = startOfDay(new Date());
    if (!minDate) minDate = addDays(now, -7);
    if (!maxDate) maxDate = addDays(now, 14);
    minDate = startOfDay(minDate);
    maxDate = startOfDay(maxDate);
    const count = Math.max(1, Math.round((maxDate - minDate) / MS_IN_DAY) + 1);
    const days = Array.from({ length: count }, (_, i) => addDays(minDate, i));
    return { days, start: minDate, end: maxDate };
  }, [data, startDateKey, endDateKey]);

  const scaleStep = dateScale === 'month' ? 30 : dateScale === 'week' ? 7 : 1;

  return (
    <div className={cn('w-full h-full overflow-auto', className)}>
      <div className={cn('min-w-[800px]')}> 
        {/* Header timeline */}
        <div className={cn('sticky top-0 z-10 flex', theme.colors.surface, 'border-b', theme.border.light)}>
          <div className={cn('w-64 px-3 py-2 text-sm font-semibold', theme.text.primary)}>
            {columns[0]?.header || columns[0]?.label || 'Item'}
          </div>
          <div className="flex-1 overflow-x-auto">
            <div className="flex">
              {days.map((d, i) => (
                <div
                  key={i}
                  className={cn('text-[10px] uppercase tracking-wider px-2 py-2 text-center border-r', theme.border.light)}
                  style={{ width: 32 * (dateScale === 'day' ? 1 : dateScale === 'week' ? 1 : 1) }}
                >
                  {d.toLocaleDateString()}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Rows */}
        <div>
          {data.map((row, idx) => {
            const s = row[startDateKey] ? startOfDay(new Date(row[startDateKey])) : null;
            const e = row[endDateKey] ? startOfDay(new Date(row[endDateKey])) : null;
            const sIdx = s ? Math.max(0, Math.round((s - start) / MS_IN_DAY)) : 0;
            const eIdx = e ? Math.min(days.length - 1, Math.round((e - start) / MS_IN_DAY)) : sIdx;
            const span = Math.max(1, (eIdx - sIdx + 1));

            return (
              <div key={idx} className={cn('flex items-stretch border-b', theme.border.light)}>
                <div className={cn('w-64 px-3 py-2 text-sm truncate', theme.text.primary)}>
                  <div className="font-semibold truncate">{String(row[columns[0]?.key] ?? '')}</div>
                  {columns[1] && (
                    <div className={cn('text-xs truncate mt-0.5', theme.text.secondary)}>
                      {String(row[columns[1].key] ?? '')}
                    </div>
                  )}
                  {/* Show file count if present */}
                  {columns.slice(2).map(col => {
                    if (col.cellRenderer === 'fileCount') {
                      const v = row[col.key];
                      return (
                        <div key={col.key} className="flex items-center gap-1 mt-1">
                          <FileIcon className="w-3 h-3 text-blue-500" />
                          <span 
                            className={`text-xs font-medium ${theme.text.primary} cursor-pointer hover:text-blue-600`} 
                            title={`${v || 0} file(s) - Click to view/download`}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('📁 File count clicked in TimelineTable:', { value: v, row, hasHandler: !!onFileCountClick });
                              if (onFileCountClick) {
                                onFileCountClick(row, v || 0);
                              } else {
                                console.log('❌ No onFileCountClick handler provided to TimelineTable');
                              }
                            }}
                          >
                            {v || 0} files
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
                <div className="flex-1 overflow-x-auto" onClick={() => onRowClick && onRowClick(row)}>
                  <div className="relative h-10">
                    {/* Bar */}
                    <div
                      className={cn('absolute top-2 h-6 rounded shadow-sm', theme.colors.primary)}
                      style={{ left: `${(sIdx / days.length) * 100}%`, width: `${(span / days.length) * 100}%` }}
                      title={`${row[columns[0]?.key] ?? ''}`}
                    />
                    {/* Grid */}
                    <div className="absolute inset-0 flex">
                      {days.map((_, i) => (
                        <div key={i} className={cn('border-r', theme.border.light)} style={{ width: `${100 / days.length}%` }} />
                      ))}
                    </div>
                  </div>
                </div>
                {/* Actions */}
                {actionsRenderer && (
                  <div className={cn(
                    'w-[120px] flex items-center justify-end px-2',
                    spacing,
                    actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
                  )} onClick={(e) => e.stopPropagation()}>
                    {actionsRenderer(row)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimelineTable;
export { TimelineTable };


